// File: frontend/src/components/marketing/landingMotion.js
//
// Shared GSAP motion helpers for the public marketing pages ("/" and
// /ambassador). GSAP itself is imported only through utils/gsapCore.js.
//
// ── PROGRESSIVE ENHANCEMENT IS THE CONTRACT ─────────────────────────────────
//
// These pages are also served as static HTML (scripts/prerender.js). The markup
// and CSS always render FINISHED content: opaque, in place, readable with
// JavaScript off, GSAP failing, or reduced motion on. Motion is layered on
// afterwards, and only for elements the reader is not looking at yet.
//
// ── WHO OWNS WHAT ───────────────────────────────────────────────────────────
//
// GSAP owns section choreography: reveals, the product scroll story, the
// network drawing, the payment sequence and the hero timeline on client-side
// navigation. Framer Motion survives only INSIDE the small chart visuals
// (visuals/*), and GSAP never targets those elements. CSS owns hover, focus
// and the hero's first-paint intro (it has to run before any JavaScript).

import { useRef, useSyncExternalStore } from "react";
import { gsap, useGSAP, ScrollTrigger, DUR, EASE, STAGGER, MQ } from "../../utils/gsapCore";

export { gsap, useGSAP, ScrollTrigger, DUR, EASE, STAGGER, MQ };

// Height of the sticky marketing header from md up (MarketingHeader).
export const HEADER_H = 68;

/**
 * Where a section runs as a scroll story. Wide enough for the two-column
 * layout, tall enough that the sticky frame fits under the header, and only for
 * visitors who have not asked for reduced motion. The same query gates each
 * story's CSS, so layout and behaviour always agree.
 *
 * ── WHY 860, MEASURED TWICE ─────────────────────────────────────────────────
 *
 * The floor was 760px and it was wrong. At 1440x768 — an ordinary laptop, well
 * inside the old query — the product tour's sticky content came to 761px in a
 * 700px box: the bottom of the browser frame sat below the fold and the last
 * bullet was cut, for the whole three-viewport story.
 *
 * Raising it to 830 was still wrong, and only measuring caught it: at exactly
 * 830 the product tour clipped by 1px and the payment story by 62px. The floor
 * has to clear the header (68) plus the TALLEST story's content, and the two
 * stories now measure 763px and 762px, so 860 leaves both about 30px of room.
 *
 * Re-measure whenever a story's content grows, and shrink the content before
 * raising this number — every 10px added here drops more real laptops onto the
 * fallback. Anything shorter gets the plain, fully readable section, which is
 * a good outcome, not a degraded one.
 */
export const STORY_MQ = "(min-width: 900px) and (min-height: 860px) and (prefers-reduced-motion: no-preference)";

const noopSubscribe = () => () => {};

/** Live boolean for a media query. False where matchMedia does not exist. */
export function useMediaMatch(query) {
  return useSyncExternalStore(
    typeof window !== "undefined" && window.matchMedia
      ? (cb) => {
          const mql = window.matchMedia(query);
          mql.addEventListener?.("change", cb);
          return () => mql.removeEventListener?.("change", cb);
        }
      : noopSubscribe,
    () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false
  );
}

export const prefersReducedMotion = () =>
  typeof window === "undefined" || !window.matchMedia || window.matchMedia(MQ.reduced).matches;

// Start states per reveal variant. `grow` is for bars and connectors: their
// transform-origin comes from their own CSS.
const FROM = {
  rise: (y) => ({ opacity: 0, y }),
  fade: () => ({ opacity: 0 }),
  grow: () => ({ scaleX: 0 }),
};

/**
 * Section reveal.
 *
 * Returns a ref for the section root. Descendants carrying `data-reveal`
 * (optionally `="fade"` or `="grow"`) reveal once, in batches, as they arrive.
 * Only elements below the fold at mount are hidden, so nothing the reader can
 * see flashes; with reduced motion nothing is touched at all.
 *
 * Opacity, never visibility: a pending element stays focusable and readable
 * by a screen reader, and keyboard focus landing on it reveals it at once.
 *
 * `deps` should carry any data that changes the section's height, so the
 * context reverts and re-measures against the real layout.
 *
 * A `data-reveal` element must not carry a CSS transform of its own; GSAP
 * writes an inline transform while it animates.
 */
export function useReveal(deps = [], { y = 18, stagger = STAGGER.base } = {}) {
  const root = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add({ reduced: MQ.reduced, always: MQ.always }, (ctx) => {
        if (ctx.conditions.reduced) return undefined;

        const el = root.current;
        if (!el || typeof window === "undefined") return undefined;

        const fold = (window.innerHeight || 0) * 0.9;
        const pending = gsap.utils
          .toArray("[data-reveal]", el)
          .filter((n) => n.getBoundingClientRect().top > fold);
        if (pending.length === 0) return undefined;

        const finish = (targets) =>
          gsap.to(targets, {
            opacity: 1,
            y: 0,
            scaleX: 1,
            duration: DUR.base,
            ease: EASE.out,
            stagger,
            overwrite: "auto",
            clearProps: "opacity,transform",
          });

        try {
          pending.forEach((n) => {
            const variant = FROM[n.getAttribute("data-reveal")] || FROM.rise;
            gsap.set(n, variant(y));
          });
          ScrollTrigger.batch(pending, {
            start: "top 94%",
            once: true,
            onEnter: (batch) => finish(batch),
          });
        } catch (err) {
          gsap.set(pending, { clearProps: "opacity,transform" });
          console.error("[marketing] reveal disabled", err);
          return undefined;
        }

        const onFocus = (e) => {
          const hit = pending.find((n) => n.contains(e.target) && Number(gsap.getProperty(n, "opacity")) < 1);
          if (hit) finish([hit]);
        };
        el.addEventListener("focusin", onFocus);
        el.setAttribute("data-motion", "ready");

        return () => {
          el.removeEventListener("focusin", onFocus);
          el.removeAttribute("data-motion");
        };
      });
    },
    { scope: root, dependencies: deps }
  );

  return root;
}

/**
 * Is this element below the fold right now? Sequences that start hidden only
 * run for sections the reader has not reached, so a reload in the middle of
 * the page never blanks what is on screen.
 */
export function isBelowFold(el, ratio = 0.85) {
  if (!el || typeof window === "undefined") return false;
  return el.getBoundingClientRect().top > (window.innerHeight || 0) * ratio;
}

/**
 * Run `onEnter` once when `el` scrolls into view, or immediately if it is
 * already in view. Returns the ScrollTrigger (killed with the GSAP context).
 */
export function playOnEnter(el, onEnter, start = "top 70%") {
  return ScrollTrigger.create({ trigger: el, start, once: true, onEnter });
}

/**
 * Pause a looping tween or timeline while `el` is off screen or the tab is
 * hidden. Returns a cleanup function.
 */
export function pauseWhenHidden(el, animation) {
  let onScreen = false;
  const sync = () => {
    if (onScreen && document.visibilityState !== "hidden") animation.play();
    else animation.pause();
  };
  const st = ScrollTrigger.create({
    trigger: el,
    start: "top bottom",
    end: "bottom top",
    onToggle: (self) => { onScreen = self.isActive; sync(); },
  });
  document.addEventListener("visibilitychange", sync);
  onScreen = st.isActive;
  sync();
  return () => {
    document.removeEventListener("visibilitychange", sync);
    st.kill();
  };
}
